import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'

import CreateTripItem from './Create'
import CreateNote from './Create/CreateNote'
import SelectAccommodationType from './Variety/SelectAccommodationType'
import SelectCruisesType from './Variety/SelectCruisesType'
import SelectFlightType from './Variety/SelectFlightType'
import SelectThingsToDoType from './Variety/SelectThingsToDoType'
import SelectTourType from './Variety/SelectTourType'
import SelectTransportType from './Variety/SelectTransportType'
import SelectBookingToAdd from '../Bookings/SelectBookingToAdd'

import { fetchOrders } from 'actions/OrderActions'
import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { useCruiseSearchEnabled } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ParentItemMetadata } from 'tripPlanner/models'
import { ParentItemType } from 'tripPlanner/types/tripItem'
import { assertUnreachable } from 'tripPlanner/utils'
import { getFutureFilteredOrdersNotInTrip } from 'tripPlanner/utils/summary'
import config from 'constants/config'
import NLPItemCreate from 'tripPlanner/components/NLPItemCreate/NLPItemCreate'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import InputWrap from 'components/Common/Form/Input/InputWrap'
import Hr from 'components/Common/Hr'
import NestableClickable from 'components/Common/NestableClickable'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineCopyIcon from 'components/Luxkit/Icons/line/LineCopyIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import ToggleButton from 'components/Luxkit/ToggleButton'
import { mediaQueryUp } from 'components/utils/breakpoint'

const EmailText = styled(BodyText)`
  width: 100%;
  padding: ${rem(12)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  padding-right: ${rem(48)};
  cursor: text;
`

const ItemTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const selectableItems: Array<ParentItemType> = [
  'LE_BOOKINGS',
  'ACCOMMODATION',
  'FLIGHT',
  'THINGS_TO_DO',
  'TOURS',
  'CRUISES',
  'TRANSPORT',
  'NOTE',
]
interface MappedStateProps {
  memberId?: string
  standaloneFlightsEnabled: boolean
}

interface Props {
  tripId: string
  defaultDate?: string
}

function SelectItemToAdd({
  tripId,
  defaultDate,
  standaloneFlightsEnabled,
}: Props & MappedStateProps) {
  const isCruiseSearchEnabled = useCruiseSearchEnabled()
  const { data: trip, isLoading: isTripLoading } = useTrip({ tripId })
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const dispatch = useAppDispatch()

  const orders = useAppSelector((state) => getUpcomingOrders(state))
  const ordersFetched = useAppSelector((state) =>
    haveOrdersBeenFetched(state, 'upcoming'),
  )

  useEffect(() => {
    if (!ordersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
    // eslint-disable-next-line
  }, [])

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersNotInTrip(orders, trip),
    [orders, trip],
  )

  const addItem = (item: (typeof selectableItems)[number]) => {
    switch (item) {
      case 'LE_BOOKINGS':
        fireInteractionEvent(addToTripClickEvent('modal_le_booking'))
        openModal(SelectBookingToAdd, { tripId })
        break
      case 'ACCOMMODATION':
        fireInteractionEvent(addToTripClickEvent('modal_accommodation'))
        openModal(SelectAccommodationType, { tripId, defaultDate })
        break
      case 'FLIGHT':
        fireInteractionEvent(addToTripClickEvent('modal_flights'))
        if (standaloneFlightsEnabled) {
          openModal(SelectFlightType, { tripId, defaultDate })
        } else {
          openModal(
            SelectFlightType,
            { tripId, customOnly: true },
            { size: 'S' },
          )
        }
        break
      case 'TOUR_AND_CRUISES':
      case 'TOURS':
        fireInteractionEvent(addToTripClickEvent('modal_tours'))
        openModal(SelectTourType, { tripId, defaultDate })
        break
      case 'CRUISES':
        fireInteractionEvent(addToTripClickEvent('modal_cruises'))
        if (isCruiseSearchEnabled) {
          openModal(SelectCruisesType, { tripId, defaultDate })
        } else {
          openModal(CreateTripItem, {
            itemType: 'CRUISES' as const,
            tripId,
          })
        }
        break
      case 'THINGS_TO_DO':
        fireInteractionEvent(addToTripClickEvent('modal_experiences'))
        openModal(SelectThingsToDoType, { tripId, defaultDate })
        break
      case 'CAR':
        fireInteractionEvent(addToTripClickEvent('modal_car_rental'))
        openModal(SelectTransportType, { tripId, defaultDate })
        break
      case 'TRANSPORT':
        fireInteractionEvent(addToTripClickEvent('modal_transport'))
        openModal(SelectTransportType, { tripId, defaultDate })
        break
      case 'NOTE':
        fireInteractionEvent(addToTripClickEvent('modal_notes'))
        openModal(CreateNote, { tripId, defaultDate })
        break
      default:
        assertUnreachable(item)
    }
  }

  const textRef = useRef<HTMLDivElement>(null)

  const emailAddress = `${config.TRIP_DOCUMENT_FORWARDING_EMAIL}`

  const copyEmailToClipboard = useCallback(() => {
    copyValueToClipboard(emailAddress)
    showSnackbar('Email copied to clipboard', 'success')
  }, [emailAddress])

  const onTextClick = useCallback(() => {
    const textDiv = textRef.current
    if (window.getSelection && textDiv) {
      const range = document.createRange()
      range.selectNode(textDiv)
      window.getSelection()!.removeAllRanges()
      window.getSelection()!.addRange(range)
    }
    copyEmailToClipboard()
  }, [copyEmailToClipboard])

  return (
    <>
      <ModalHeader
        title="Add to trip"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {config.ENABLE_TRIP_DOCUMENT_EMAIL_FORWARDING && <>
              <VerticalSpacer gap={16}>
                <div>
                  <BodyText variant="large" weight="bold">
                    Forward email
                  </BodyText>
                  <Caption variant="large">
                    Forward any booking confirmations or documents from the email you logged in with, and we’ll add them to your trip.
                  </Caption>
                </div>

                <NestableClickable onClick={onTextClick}>
                  <InputWrap endIcon={<IconButton onClick={copyEmailToClipboard} kind="tertiary"><LineCopyIcon /></IconButton>} noValidationSpacing>
                    <div ref={textRef}>
                      <EmailText variant="medium" wrap="word-wrap">{emailAddress}</EmailText>
                    </div>
                  </InputWrap>
                </NestableClickable>
              </VerticalSpacer>

              <div>
                <Hr />
              </div>
            </>}

            {config.ENABLE_TRIP_ITEM_NLP && trip && <>
              <VerticalSpacer gap={16}>
                <BodyText variant="large" weight="bold">
                  Type your plan to add an item
                </BodyText>
                <NLPItemCreate trip={trip} onItemCreated={closeModal} date={defaultDate} dropdownAutoHeight />
              </VerticalSpacer>

              <div>
                <Hr />
              </div>
            </>}

            <VerticalSpacer gap={16}>
              <BodyText variant="large" weight="bold">
                Add an individual item
              </BodyText>
              <ItemTypesGrid>
                {selectableItems.map((item) => {
                  const { icon: Icon, name } = ParentItemMetadata[item]

                  if (item === 'LE_BOOKINGS') {
                    if (filteredOrders.length < 1) {
                      return null
                    }
                    return (
                      <ToggleButton
                        key={item}
                        kind="secondary"
                        startIcon={<Icon size="S" colour="neutral-one" />}
                        title={`${name} ${
                          !isTripLoading ? `(${filteredOrders.length})` : ''
                        }`}
                        onClick={() => addItem(item)}
                      />
                    )
                  }

                  return (
                    <ToggleButton
                      key={item}
                      kind="secondary"
                      startIcon={<Icon size="S" />}
                      title={name}
                      onClick={() => addItem(item)}
                    />
                  )
                })}
              </ItemTypesGrid>
            </VerticalSpacer>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default connect<MappedStateProps, unknown, Props, App.State>(
  (state) => ({
    memberId: state.auth.account.memberId,
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state),
  }),
)(SelectItemToAdd)
